import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Baby, CalendarDays, Sparkles } from "lucide-react";

const DAY = 24 * 60 * 60 * 1000;

// Gestational week (from LMP) -> baby size comparison
const FRUIT_BY_WEEK = {
  4: { emoji: "🌱", label: "a poppy seed" },
  5: { emoji: "🍎", label: "an apple seed" },
  6: { emoji: "🫛", label: "a sweet pea" },
  7: { emoji: "🫐", label: "a blueberry" },
  8: { emoji: "🫘", label: "a kidney bean" },
  9: { emoji: "🍒", label: "a cherry" },
  10: { emoji: "🍓", label: "a strawberry" },
  11: { emoji: "🫒", label: "a fig" },
  12: { emoji: "🍋‍🟩", label: "a lime" },
  13: { emoji: "🍋", label: "a lemon" },
  14: { emoji: "🍑", label: "a peach" },
  15: { emoji: "🍎", label: "an apple" },
  16: { emoji: "🥑", label: "an avocado" },
  17: { emoji: "🍐", label: "a pear" },
  18: { emoji: "🫑", label: "a bell pepper" },
  19: { emoji: "🥭", label: "a mango" },
  20: { emoji: "🍌", label: "a banana" },
  21: { emoji: "🥕", label: "a carrot" },
  22: { emoji: "🥭", label: "a papaya" },
  23: { emoji: "🍆", label: "a small eggplant" },
  24: { emoji: "🌽", label: "an ear of corn" },
  25: { emoji: "🥬", label: "a rutabaga" },
  26: { emoji: "🥬", label: "a head of lettuce" },
  27: { emoji: "🥦", label: "a cauliflower" },
  28: { emoji: "🍆", label: "a large eggplant" },
  29: { emoji: "🎃", label: "a butternut squash" },
  30: { emoji: "🥬", label: "a cabbage" },
  31: { emoji: "🥥", label: "a coconut" },
  32: { emoji: "🎃", label: "a squash" },
  33: { emoji: "🍍", label: "a pineapple" },
  34: { emoji: "🍈", label: "a cantaloupe" },
  35: { emoji: "🍈", label: "a honeydew melon" },
  36: { emoji: "🥬", label: "a head of romaine" },
  37: { emoji: "🥬", label: "a bunch of Swiss chard" },
  38: { emoji: "🥬", label: "a leek" },
  39: { emoji: "🍉", label: "a mini watermelon" },
  40: { emoji: "🎃", label: "a small pumpkin" },
};

const sizeForWeek = (weeks) => {
  if (weeks < 4) return { emoji: "✨", label: "too tiny to measure yet" };
  if (weeks > 40) return { emoji: "🎉", label: "ready to meet you" };
  return FRUIT_BY_WEEK[weeks] || FRUIT_BY_WEEK[40];
};

const fmtDate = (d) =>
  d.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });

const trimesterOf = (weeks) => {
  if (weeks < 14) return "First trimester";
  if (weeks < 28) return "Second trimester";
  return "Third trimester";
};

export const PregnancyCalculator = () => {
  const [lmp, setLmp] = useState("");
  const [cycle, setCycle] = useState(28);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const calculate = (e) => {
    e.preventDefault();
    setError("");
    setResult(null);
    if (!lmp) {
      setError("Please choose the first day of your last period.");
      return;
    }
    const start = new Date(lmp + "T00:00:00");
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (isNaN(start.getTime())) {
      setError("That date doesn't look right — please try again.");
      return;
    }
    if (start.getTime() > today.getTime()) {
      setError("That date is in the future. Please pick the first day of your last period.");
      return;
    }
    const cyc = Math.min(Math.max(Number(cycle) || 28, 20), 45);
    const dueDate = new Date(start.getTime() + 280 * DAY + (cyc - 28) * DAY);
    const gestDays = Math.floor((today.getTime() - start.getTime()) / DAY);

    if (gestDays > 45 * 7) {
      setError("That's more than 45 weeks ago — please double-check the date.");
      return;
    }

    const weeks = Math.floor(gestDays / 7);
    const days = gestDays % 7;
    const daysToGo = Math.round((dueDate.getTime() - today.getTime()) / DAY);
    const totalPct = Math.min(Math.max((gestDays / 280) * 100, 0), 100);

    setResult({ dueDate, weeks, days, daysToGo, trimester: trimesterOf(weeks), pct: Math.round(totalPct), size: sizeForWeek(weeks) });
  };

  return (
    <section className="max-w-4xl mx-auto px-5 py-16" data-testid="pregnancy-calculator">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="relative overflow-hidden rounded-3xl border border-border bg-card p-8 sm:p-10 soft-shadow"
      >
        <div className="blob bg-accent h-56 w-56 -top-16 -right-10 opacity-60" />
        <div className="relative">
          <span className="inline-flex items-center gap-2 rounded-full bg-accent px-4 py-1.5 text-sm font-semibold text-accent-foreground mb-5">
            <Baby className="h-4 w-4" /> Free tool for expecting mamas
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-2">Pregnancy Due Date Calculator</h2>
          <p className="text-muted-foreground mb-8 max-w-xl">
            Find your estimated due date and see how far along you are. Just enter the first day of your last period.
          </p>

          <form onSubmit={calculate} className="grid gap-5 sm:grid-cols-2 items-end">
            <div>
              <label htmlFor="lmp-date" className="block text-sm font-semibold mb-2">
                First day of your last period
              </label>
              <input
                id="lmp-date"
                type="date"
                value={lmp}
                max={new Date().toISOString().split("T")[0]}
                onChange={(e) => setLmp(e.target.value)}
                data-testid="calc-lmp-input"
                className="w-full rounded-xl border border-border bg-background px-4 py-3 text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label htmlFor="cycle-length" className="block text-sm font-semibold mb-2">
                Average cycle length (days)
              </label>
              <input
                id="cycle-length"
                type="number"
                min="20"
                max="45"
                value={cycle}
                onChange={(e) => setCycle(e.target.value)}
                data-testid="calc-cycle-input"
                className="w-full rounded-xl border border-border bg-background px-4 py-3 text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div className="sm:col-span-2">
              <button
                type="submit"
                data-testid="calc-submit"
                className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-bold px-7 py-3.5 hover:-translate-y-0.5 transition-transform soft-shadow"
              >
                <CalendarDays className="h-4 w-4" /> Calculate my due date
              </button>
            </div>
          </form>

          {error && (
            <p className="mt-5 text-sm font-medium text-destructive" data-testid="calc-error">
              {error}
            </p>
          )}

          <AnimatePresence>
            {result && (
              <motion.div
                key="calc-result"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.45, ease: "easeOut" }}
                className="mt-8"
                data-testid="calc-result"
              >
                <div className="rounded-2xl bg-accent/60 border border-border p-6">
                  <div className="flex items-center gap-2 text-primary mb-1">
                    <Sparkles className="h-5 w-5" />
                    <span className="text-sm font-bold uppercase tracking-wider">Estimated due date</span>
                  </div>
                  <p className="font-heading text-2xl sm:text-3xl font-bold" data-testid="calc-due-date">
                    {fmtDate(result.dueDate)}
                  </p>

                  <div
                    className="mt-5 flex items-center gap-4 rounded-2xl bg-card border border-border p-4"
                    data-testid="calc-baby-size"
                  >
                    <span className="text-4xl sm:text-5xl leading-none" aria-hidden="true">
                      {result.size.emoji}
                    </span>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Your baby is about the size of
                      </p>
                      <p className="font-heading text-xl sm:text-2xl font-bold capitalize" data-testid="calc-baby-size-label">
                        {result.size.label}
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 grid gap-4 sm:grid-cols-3">
                    <div className="rounded-xl bg-card border border-border p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">How far along</p>
                      <p className="font-heading text-xl font-bold mt-1" data-testid="calc-weeks">
                        {result.weeks}w {result.days}d
                      </p>
                    </div>
                    <div className="rounded-xl bg-card border border-border p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Trimester</p>
                      <p className="font-heading text-xl font-bold mt-1" data-testid="calc-trimester">
                        {result.trimester}
                      </p>
                    </div>
                    <div className="rounded-xl bg-card border border-border p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Weeks/days to go</p>
                      <p className="font-heading text-xl font-bold mt-1" data-testid="calc-days-to-go">
                        {result.daysToGo > 0 ? `${Math.floor(result.daysToGo / 7)}w ${result.daysToGo % 7}d` : "Any day now! 💛"}
                      </p>
                    </div>
                  </div>

                  <div className="mt-6">
                    <div className="flex justify-between text-xs font-semibold text-muted-foreground mb-1.5">
                      <span>Your journey</span>
                      <span>{result.pct}%</span>
                    </div>
                    <div className="h-2.5 w-full rounded-full bg-border overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${result.pct}%` }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                        className="h-full rounded-full bg-primary"
                      />
                    </div>
                  </div>

                  <p className="text-xs text-muted-foreground mt-5">
                    This is an estimate based on a standard 40-week pregnancy. Only about 1 in 20 babies arrive on their
                    exact due date — always confirm with your doctor or midwife.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </section>
  );
};

export default PregnancyCalculator;
